import React, { useCallback, useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { Alert, Badge, Button, Table } from "react-bootstrap"
import { toast } from "react-toastify"
import moment from "moment"
import { roleCheck } from "utils/index"
import { SURVEYORS } from "utils/constants"
import { getCurrentUser, getCurrentRole, getBaseUrl } from "../../../reducers"
import NavBar from "../shared/NavBar"
import {
  getLeaveApplications,
  parseLeaveApplicationAttributes,
  removedFromProfileCellProps
} from "requesters/leaveApplicationRequester"
import AnnualLeaveFormModal from "./AnnualLeaveFormModal"

function parseApplications(payload) {
  if (!payload?.data) return []
  const list = Array.isArray(payload.data) ? payload.data : [payload.data]
  return list.map(row => {
    const a = row.attributes || {}
    return {
      id: row.id,
      ...parseLeaveApplicationAttributes(a)
    }
  })
}

function formatLeaveDateTime(value) {
  if (!value) return "—"
  const parsed = moment(value)
  return parsed.isValid() ? parsed.format("DD/MM/YYYY, hh:mm A") : value
}

function statusBadgeVariant(status) {
  if (status === "approved") return "success"
  if (status === "declined") return "danger"
  return "warning"
}

const AnnualLeaveForm = () => {
  const baseUrl = useSelector(getBaseUrl)
  const currentUser = useSelector(getCurrentUser)
  const currentRole = useSelector(getCurrentRole)
  const systemRole = currentUser?.attributes?.role
  const [applications, setApplications] = useState([])
  const [loadingList, setLoadingList] = useState(true)
  const [showFormModal, setShowFormModal] = useState(false)
  const [editingApplication, setEditingApplication] = useState(null)

  const loadApplications = useCallback(() => {
    if (!baseUrl) return Promise.resolve()
    setLoadingList(true)
    return getLeaveApplications(baseUrl)
      .then(res => {
        if (res.status === 200 && res.response) {
          setApplications(parseApplications(res.response))
        } else if (res.error) {
          toast.error("Could not load leave applications.")
        }
      })
      .finally(() => setLoadingList(false))
  }, [baseUrl])

  useEffect(() => {
    loadApplications()
  }, [loadApplications])

  const openNewApplication = () => {
    setEditingApplication(null)
    setShowFormModal(true)
  }

  const openEditApplication = row => {
    setEditingApplication(row)
    setShowFormModal(true)
  }

  const closeFormModal = () => {
    setShowFormModal(false)
    setEditingApplication(null)
  }

  if (!roleCheck(systemRole, currentRole, SURVEYORS)) {
    window.location = "/"
    return null
  }

  const declinedItems = applications.filter(a => a.status === "declined")

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-md-3 col-xl-2">
          <NavBar />
        </div>
        <div className="col-md-8 col-xl-10">
          <h1 className="mb-4">Leave Request</h1>

          <p className="text-muted mb-3">
            Submit your leave request for admin review.
          </p>

          <div className="d-flex justify-content-end mb-4">
            <Button type="button" variant="primary" onClick={openNewApplication}>
              New leave application
            </Button>
          </div>

          {declinedItems.length > 0 && (
            <Alert variant="warning" className="mb-3">
              <div className="font-weight-bold mb-1">
                {declinedItems.length}
                {" "}
                application
                {declinedItems.length > 1 ? "s were" : " was"}
                {" "}
                declined
              </div>
              {declinedItems.map(item => (
                <div key={item.id} className="small mb-1">
                  {item.periodStartLabel || formatLeaveDateTime(item.startAt)}
                  {" "}
                  –
                  {" "}
                  {item.periodEndLabel || formatLeaveDateTime(item.endAt)}
                  :
                  {" "}
                  {item.declinedReason || "No reason provided."}
                </div>
              ))}
            </Alert>
          )}

          <h4 className="mb-3">My submissions</h4>
          {loadingList ? (
            <p>Loading…</p>
          ) : applications.length === 0 ? (
            <p className="text-muted">No submissions yet. Use &quot;New leave application&quot; to submit a request.</p>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Reason</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Days off</th>
                  <th>Submitted</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {applications.map(row => (
                  <tr key={row.id}>
                    <td>
                      <Badge variant={statusBadgeVariant(row.status)}>{row.status}</Badge>
                      {row.blockOutRemoved && (
                        <Badge variant="secondary" className="ml-1">Removed from profile</Badge>
                      )}
                    </td>
                    <td {...removedFromProfileCellProps(row.blockOutRemoved)}>{row.leaveTypeLabel}</td>
                    <td {...removedFromProfileCellProps(row.blockOutRemoved)}>
                      {row.periodStartLabel || formatLeaveDateTime(row.startAt)}
                    </td>
                    <td {...removedFromProfileCellProps(row.blockOutRemoved)}>
                      {row.periodEndLabel || formatLeaveDateTime(row.endAt)}
                    </td>
                    <td {...removedFromProfileCellProps(row.blockOutRemoved)}>
                      {row.daysOffLabel ?? row.workingDaysOff}
                    </td>
                    <td {...removedFromProfileCellProps(row.blockOutRemoved)}>
                      {row.submittedAt ? new Date(row.submittedAt).toLocaleDateString() : "—"}
                    </td>
                    <td {...removedFromProfileCellProps(row.blockOutRemoved)}>
                      {row.status === "declined" ? (row.declinedReason || "—") : "—"}
                    </td>
                    <td>
                      {row.status === "pending" ? (
                        <Button
                          type="button"
                          variant="outline-primary"
                          size="sm"
                          onClick={() => openEditApplication(row)}
                        >
                          Edit
                        </Button>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </div>

      <AnnualLeaveFormModal
        show={showFormModal}
        onHide={closeFormModal}
        onSubmitted={loadApplications}
        application={editingApplication}
      />
    </div>
  )
}

export default AnnualLeaveForm
